import { createFileRoute, useNavigate, Link } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { Loader2 } from 'lucide-react'
import { supabase } from '@/lib/supabase'
import { btnPrimary } from '@/components/rm'
import { BrandMark } from '@/components/brand'

export const Route = createFileRoute('/join')({
  validateSearch: (s: Record<string, unknown>) => ({ token: String(s.token ?? '') }),
  component: JoinPage,
})

const inputCls =
  'h-10 w-full rounded-[10px] border border-token-subtle px-3 text-[14px] text-token-primary outline-none transition-colors focus:border-token-strong focus:ring-2 focus:ring-brand/10'

interface Peek { valid: boolean; email: string; role: string; company: string; reason?: string | null }

function JoinPage() {
  const { token } = Route.useSearch()
  const navigate = useNavigate()
  const [peek, setPeek] = useState<Peek | null>(null)
  const [loading, setLoading] = useState(true)
  const [f, setF] = useState({ firstName: '', lastName: '', password: '' })
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (!token) { setLoading(false); return }
    supabase.functions.invoke('invite-accept', { body: { token } }).then(({ data }) => {
      setPeek(data as Peek); setLoading(false)
    }).catch(() => setLoading(false))
  }, [token])

  const accept = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    if (!f.firstName || f.password.length < 8) return setError('Enter your name and a password of at least 8 characters.')
    setBusy(true)
    const { data, error: err } = await supabase.functions.invoke('invite-accept', { body: { token, ...f } })
    if (err || !(data as { ok?: boolean })?.ok) {
      setBusy(false)
      const ctx = (err as { context?: Response } | null)?.context
      let msg = 'Could not accept the invite.'
      try { if (ctx) { const j = await ctx.json(); if (j?.error) msg = j.error } } catch { /* */ }
      return setError(msg)
    }
    const { error: signErr } = await supabase.auth.signInWithPassword({ email: peek!.email, password: f.password })
    setBusy(false)
    navigate({ to: signErr ? '/login' : '/dashboard' })
  }

  const Shell = ({ children }: { children: React.ReactNode }) => (
    <div className="flex min-h-screen items-center justify-center bg-canvas-subtle px-4 py-10">
      <div className="w-full max-w-sm">
        <div className="mb-6 text-center">
          <BrandMark className="mx-auto mb-3 h-11 w-11" />
        </div>
        {children}
      </div>
    </div>
  )

  if (loading) return <Shell><div className="text-center"><Loader2 className="mx-auto h-6 w-6 animate-spin text-token-muted" /></div></Shell>

  if (!token || !peek || !peek.valid) {
    return <Shell>
      <div className="rounded-[16px] border border-token-subtle bg-token-surface p-6 text-center">
        <h1 className="font-display text-[20px] font-bold text-token-primary">Invite unavailable</h1>
        <p className="mt-2 text-[13.5px] text-token-secondary">{peek?.reason === 'used' ? 'This invitation has already been used.' : 'This invitation link is invalid or has expired.'}</p>
        <Link to="/login" className="mt-4 inline-block text-[13px] font-semibold text-token-primary hover:underline">Go to sign in</Link>
      </div>
    </Shell>
  }

  return <Shell>
    <div className="mb-5 text-center">
      <h1 className="font-display text-[22px] font-bold tracking-[-0.02em] text-token-primary">Join {peek.company}</h1>
      <p className="mt-1 text-[13.5px] text-token-secondary">Set up your account to get started.</p>
    </div>
    <form onSubmit={accept} className="space-y-3.5 rounded-[16px] border border-token-subtle bg-token-surface p-6">
      <div className="space-y-1.5">
        <label className="text-[13px] font-semibold text-token-secondary">Email</label>
        <input value={peek.email} disabled className={`${inputCls} bg-canvas-subtle text-token-secondary`} />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5"><label className="text-[13px] font-semibold text-token-secondary">First name</label><input value={f.firstName} onChange={(e) => setF({ ...f, firstName: e.target.value })} autoComplete="given-name" className={inputCls} /></div>
        <div className="space-y-1.5"><label className="text-[13px] font-semibold text-token-secondary">Last name</label><input value={f.lastName} onChange={(e) => setF({ ...f, lastName: e.target.value })} autoComplete="family-name" className={inputCls} /></div>
      </div>
      <div className="space-y-1.5"><label className="text-[13px] font-semibold text-token-secondary">Password</label><input type="password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} autoComplete="new-password" className={inputCls} placeholder="At least 8 characters" /></div>
      {error && <p className="text-[13px] text-rose-600">{error}</p>}
      <button type="submit" disabled={busy} className={`${btnPrimary} h-10 w-full justify-center`}>{busy ? 'Setting up…' : 'Join & continue'}</button>
    </form>
  </Shell>
}
